// src/pages/ReferralPage.js
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase/firestore";
import { useUser } from "../context/userContext";
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where,
  orderBy,
  limit
} from "firebase/firestore";
import { 
  Share2, 
  Copy, 
  Users, 
  DollarSign, 
  CheckCircle,
  Gift,
  UserPlus,
  RefreshCw,
  AlertCircle,
  ChevronRight,
  ArrowUpRight,
  ExternalLink,
  ShoppingBag
} from "lucide-react";

const ReferralPage = () => {
  const navigate = useNavigate();
  const { 
    id: userId, 
    loading: userLoading, 
    initialized, 
    telegramUser,
    telegramData,
    refreshUserData,
    balance,
    refBonus,
    referrals: contextReferrals // Получаем рефералы из контекста
  } = useUser();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalReferrals: 0,
    totalEarned: 0,
    pendingAmount: 0,
    conversionRate: 0
  });
  
  const [referrals, setReferrals] = useState([]);
  const [usernamesCache, setUsernamesCache] = useState({});
  const [referralLink, setReferralLink] = useState("");
  const [copied, setCopied] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  
  const log = (message, data = null) => {
    console.log(`[ReferralPage] ${message}`, data || '');
  };

  const getUsernameByUserId = useCallback(async (uid) => {
  if (!uid) return null;

  // если уже есть в кэше — возвращаем
  if (usernamesCache[uid]) {
    return usernamesCache[uid];
  }

  try {
    const userRef = doc(db, "telegramUsers", uid.toString());
    const snap = await getDoc(userRef);

    if (snap.exists()) {
      const data = snap.data();
      const username =
        data.username ||
        data.firstName ||
        data.name ||
        null;

      setUsernamesCache(prev => ({
        ...prev,
        [uid]: username
      }));

      return username;
    }
  } catch (e) {
    console.error("Ошибка загрузки username", uid, e);
  }

  return null;
}, [usernamesCache]);


  // Telegram back button
  useEffect(() => {
    if (window.Telegram?.WebApp) {
      const tg = window.Telegram.WebApp;
      tg.BackButton.show();
      tg.BackButton.onClick(() => {
        log("Кнопка назад нажата");
        navigate(-1);
      });
      
      return () => {
        tg.BackButton.hide();
        tg.BackButton.offClick();
      };
    }
  }, [navigate]);

  // Функция для генерации реферальной ссылки для Telegram
  const generateTelegramReferralLink = useCallback((userId) => {
    if (!userId) return "";
    return `https://t.me/duck_pekin_bot?start=${userId}`;
  }, []);

  // Генерация реферальной ссылки
  useEffect(() => {
    const effectiveUserId = userId || telegramData?.id || telegramUser?.id;
    
    if (effectiveUserId) {
      const link = generateTelegramReferralLink(effectiveUserId);
      setReferralLink(link);
      log("Ссылка сгенерирована", { 
        userId: effectiveUserId, 
        link,
        format: "telegram_bot" 
      });
    } else {
      log("Нет данных для генерации ссылки");
      setReferralLink("");
    }
  }, [userId, telegramData, telegramUser, generateTelegramReferralLink]);

  // Функция для преобразования рефералов из контекста в нужный формат
const formatContextReferrals = useCallback((refs, usernamesMap = {}) => {
  if (!Array.isArray(refs)) return [];

  return refs.map((ref, index) => {
    const uid = ref.userId?.toString();

    const username =
      ref.username ||
      usernamesMap[uid] ||
      null;

    return {
      id: uid || `ref-${index}`,
      name: username || "Пользователь",
      username: username,
      joinedAt: new Date(),
      hasOrders: (ref.balance || 0) > 0,
      totalSpent: ref.balance || 0
    };
  });
}, []);



  // Загрузка данных - ПРОБУЕМ 3 ИСТОЧНИКА ДАННЫХ
  useEffect(() => {
 const loadData = async () => {
  const effectiveUserId =
    userId || telegramData?.id || telegramUser?.id;

  if (!effectiveUserId || !initialized) {
    log("Ожидание инициализации", { effectiveUserId, initialized });
    setLoading(false);
    return;
  }

  setLoading(true);
  setError(null);

  try {
    // ======================================================
    // 1. ЗАГРУЖАЕМ РЕФЕРАЛОВ ИЗ FIRESTORE (ГЛАВНОЕ!)
    // ======================================================
    const referralsQuery = query(
      collection(db, "telegramUsers"),
      where("referrerId", "==", effectiveUserId.toString()),
      orderBy("joinedAt", "desc")
    );

    const snap = await getDocs(referralsQuery);

    const firestoreReferrals = [];

    snap.forEach((docSnap) => {
      const data = docSnap.data();

      firestoreReferrals.push({
        id: docSnap.id,
        name:
          data.fullName ||
          data.username ||
          "Пользователь",
        username: data.username || null,
        joinedAt: data.joinedAt
          ? new Date(data.joinedAt)
          : new Date(),
        hasOrders: (data.totalOrders || 0) > 0,
        totalSpent: data.totalSpent || 0
      });
    });

    log("Рефералы из Firestore", {
      count: firestoreReferrals.length,
      users: firestoreReferrals.map(r => ({
        id: r.id,
        name: r.name,
        username: r.username
      }))
    });

    // ======================================================
    // 2. СТАТИСТИКА ТЕКУЩЕГО ПОЛЬЗОВАТЕЛЯ
    // ======================================================
    const userRef = doc(
      db,
      "telegramUsers",
      effectiveUserId.toString()
    );
    const userSnap = await getDoc(userRef);

    let totalEarned = 0;
    let pendingAmount = 0;

    if (userSnap.exists()) {
      const data = userSnap.data();
      totalEarned = data.totalEarned || data.balance || 0;
      pendingAmount = data.refBonus || 0;
    }

    const activeReferrals = firestoreReferrals.filter(
      r => r.hasOrders
    ).length;

    const conversionRate =
      firestoreReferrals.length > 0
        ? Math.round(
            (activeReferrals / firestoreReferrals.length) * 100
          )
        : 0;

    // ======================================================
    // 3. SET STATE
    // ======================================================
    setReferrals(firestoreReferrals);

    setStats({
      totalReferrals: firestoreReferrals.length,
      totalEarned,
      pendingAmount,
      conversionRate
    });

    log("Финальные данные", {
      referralsCount: firestoreReferrals.length,
      conversionRate
    });
  } catch (e) {
    console.error("[ReferralPage] loadData error", e);
    setError("Не удалось загрузить рефералов");
  } finally {
    setLoading(false);
  }
};


    
    const timer = setTimeout(loadData, 500);
    return () => clearTimeout(timer);
  }, [userId, telegramData, telegramUser, initialized, balance, refBonus, contextReferrals, formatContextReferrals]);

  // Копирование ссылки
  const copyReferralLink = useCallback(() => {
    if (!referralLink) {
      alert("Ссылка еще не готова");
      return;
    }
    
    navigator.clipboard.writeText(referralLink)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        
        if (window.Telegram?.WebApp) {
          window.Telegram.WebApp.showAlert("Ссылка скопирована в буфер обмена!");
        }
      })
      .catch(() => {
        alert("Не удалось скопировать ссылку");
      });
  }, [referralLink]);

  // Поделиться в Telegram
  const shareInTelegram = useCallback(() => {
    if (!referralLink) {
      alert("Ссылка не готова");
      return;
    }
    
    if (window.Telegram?.WebApp) {
      const shareText = "🍗 Заказывай утку по-пекински через нашего бота! \n\n" +
                       "По моей ссылке получишь специальный подарок на первый заказ! \n\n" +
                       "Переходи и заказывай вкуснейшую утку 🦆";
      
      window.Telegram.WebApp.openTelegramLink(
        `https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent(shareText)}`
      );
    } else {
      window.open(`tg://msg?text=${encodeURIComponent(referralLink)}`, '_blank');
    }
  }, [referralLink]);

  // Обновить данные
  const refreshData = useCallback(() => {
    setLoading(true);
    refreshUserData?.();
    setTimeout(() => setLoading(false), 1500);
  }, [refreshUserData]);

  // Форматирование
  const formatAmount = (amount) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    try {
      if (!date) return "Недавно";
      const dateObj = new Date(date);
      const now = new Date();
      const diffTime = Math.abs(now - dateObj);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return "Сегодня";
      if (diffDays === 1) return "Вчера";
      if (diffDays < 7) return `${diffDays} дн. назад`;
      
      return dateObj.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'short'
      });
    } catch {
      return "Недавно";
    }
  };

  // Скелетон загрузки
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pb-24 safe-area-bottom">
        {/* Шапка скелетон */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 pt-16 pb-24 px-4">
          <div className="max-w-md mx-auto">
            <div className="h-6 bg-white/20 rounded mb-3 animate-pulse"></div>
            <div className="h-4 bg-white/20 rounded w-3/4 animate-pulse"></div>
          </div>
        </div>
        
        {/* Контент скелетон */}
        <div className="max-w-md mx-auto px-4 -mt-16">
          {/* Статистика скелетон */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-2xl shadow-sm p-4">
                <div className="h-4 bg-gray-200 rounded mb-3 animate-pulse"></div>
                <div className="h-8 bg-gray-300 rounded animate-pulse"></div>
              </div>
            ))}
          </div>
          
          {/* Ссылка скелетон */}
          <div className="bg-white rounded-2xl shadow-sm p-5 mb-6">
            <div className="h-6 bg-gray-200 rounded mb-4 animate-pulse"></div>
            <div className="h-12 bg-gray-100 rounded mb-4 animate-pulse"></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="h-12 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-12 bg-gray-200 rounded animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Показываем ошибку
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-16 pb-24 safe-area-bottom">
        <div className="max-w-md mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl shadow-sm p-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <div className="text-red-500 text-lg mb-2">Ошибка загрузки</div>
            <p className="text-gray-600 mb-4 text-sm">{error}</p>
            <button
              onClick={refreshData}
              className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-6 py-3 rounded-xl font-medium hover:shadow-lg transition-all active:scale-95"
            >
              Попробовать снова
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pb-24 safe-area-bottom">
      {/* Шапка */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white pt-16 pb-24 px-4 safe-area-top">
        <div className="max-w-md mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold">Партнёрская программа</h1>
              <p className="text-orange-100 opacity-95 mt-1">
                Приглашайте друзей и получайте 10% с заказов
              </p>
            </div>
            <button
              onClick={refreshData}
              className="bg-white/20 hover:bg-white/30 p-3 rounded-full transition-all active:scale-95"
              title="Обновить"
            >
              <RefreshCw className="h-5 w-5" />
            </button>
          </div>
          
          {/* Статистика */}
          <div className="grid grid-cols-2 gap-3 mt-8">
            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 text-center border border-white/30">
              <div className="text-2xl font-bold">{stats.totalReferrals}</div>
              <div className="text-xs opacity-90 mt-1">приглашено</div>
            </div>
            
            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 text-center border border-white/30">
              <div className="text-2xl font-bold">{formatAmount(stats.totalEarned)}</div>
              <div className="text-xs opacity-90 mt-1">заработано</div>
            </div>
            
            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 text-center border border-white/30 col-span-2">
              <div className="flex items-center justify-center space-x-4">
                <div className="text-center">
                  <div className="text-2xl font-bold">{formatAmount(stats.pendingAmount)}</div>
                  <div className="text-xs opacity-90 mt-1">доступно</div>
                </div>
                {stats.conversionRate > 0 && (
                  <>
                    <div className="h-10 w-px bg-white/30"></div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">{stats.conversionRate}%</div>
                      <div className="text-xs opacity-90 mt-1">конверсия</div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Основной контент */}
      <div className="max-w-md mx-auto px-4 -mt-16 space-y-4">
        {/* Реферальная ссылка */}
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <div className="flex items-center mb-5">
            <div className="bg-gradient-to-r from-orange-100 to-amber-100 p-3 rounded-xl mr-3">
              <Share2 className="h-6 w-6 text-orange-500" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Ваша партнёрская ссылка</h2>
              <p className="text-sm text-gray-500">Делитесь с друзьями</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="relative">
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 pr-12 text-sm font-mono break-all">
                {referralLink || "Ссылка загружается..."}
              </div>
              <button
                onClick={copyReferralLink}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 active:scale-95"
              >
                <Copy className="h-5 w-5" />
              </button>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={copyReferralLink}
                disabled={!referralLink}
                className={`py-3.5 px-4 rounded-xl flex items-center justify-center font-medium transition-all active:scale-95 ${
                  copied
                    ? 'bg-green-100 text-green-700 border border-green-200'
                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                }`}
              >
                {copied ? (
                  <>
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Скопировано
                  </>
                ) : (
                  <>
                    <Copy className="h-5 w-5 mr-2" />
                    Копировать
                  </>
                )}
              </button>
              
              <button
                onClick={shareInTelegram}
                disabled={!referralLink}
                className="bg-gradient-to-r from-orange-500 to-amber-500 text-white py-3.5 px-4 rounded-xl flex items-center justify-center font-medium hover:shadow-lg transition-all active:scale-95 disabled:opacity-50"
              >
                <Share2 className="h-5 w-5 mr-2" />
                Поделиться
              </button>
            </div>
          </div>
        </div>

        {/* Рефералы */}
        {referrals.length > 0 ? (
          <div className="bg-white rounded-2xl shadow-sm p-5">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center">
                <div className="bg-gradient-to-r from-green-100 to-emerald-100 p-3 rounded-xl mr-3">
                  <UserPlus className="h-6 w-6 text-green-500" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">Ваши рефералы</h2>
                  <p className="text-sm text-gray-500">
                    {stats.totalReferrals} человек • {stats.conversionRate}% конверсия
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium text-gray-800">
                  {formatAmount(stats.totalEarned)}
                </div>
                <div className="text-xs text-gray-500">заработано</div>
              </div>
            </div>
            
            <div className="space-y-3 max-h-96 overflow-y-auto pr-2 -mr-2">
              {referrals.map((referral, index) => (
                <div 
                  key={referral.id || index} 
                  className="flex items-center p-4 border border-gray-100 rounded-xl hover:bg-gray-50 transition-colors active:bg-gray-100"
                >
                  <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full flex items-center justify-center mr-4">
                    <span className="font-bold text-gray-600 text-xl">
                      {referral.name?.[0]?.toUpperCase() || "П"}
                    </span>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-medium text-gray-800 truncate">
                        {referral.name}
                      </h4>
                      {referral.hasOrders ? (
                        <span className="flex items-center bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs px-3 py-1.5 rounded-full ml-2">
                          <ShoppingBag className="h-3 w-3 mr-1" />
                          Заказ
                        </span>
                      ) : (
                        <span className="text-xs text-gray-500 px-3 py-1.5">
                          Ожидает
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-500">
                          {formatDate(referral.joinedAt)}
                        </span>
                        {referral.username && (
                          <span className="text-xs text-gray-400">@{referral.username}</span>
                        )}
                      </div>
                      
                      {referral.totalSpent > 0 && (
                        <div className="text-right">
                          <div className="text-sm font-medium text-green-600">
                            +{formatAmount(referral.totalSpent * 0.1)}
                          </div>
                          <div className="text-xs text-gray-500">
                            с {formatAmount(referral.totalSpent)}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-6 pt-4 border-t border-gray-100 text-center">
              <p className="text-sm text-gray-500">
                Показано {referrals.length} из {stats.totalReferrals} рефералов
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
            <div className="w-20 h-20 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
              <Users className="h-10 w-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-medium text-gray-700 mb-3">
              Пока нет рефералов
            </h3>
            <p className="text-gray-500 mb-6 max-w-xs mx-auto">
              Пригласите друзей по своей ссылке и они появятся здесь
            </p>
            <div className="space-y-3">
              <button
                onClick={copyReferralLink}
                className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white px-6 py-4 rounded-xl font-medium hover:shadow-lg transition-all active:scale-95 inline-flex items-center justify-center"
              >
                <Share2 className="h-5 w-5 mr-2" />
                Скопировать ссылку
              </button>
              <button
                onClick={shareInTelegram}
                className="w-full border border-orange-500 text-orange-500 px-6 py-4 rounded-xl font-medium hover:bg-orange-50 transition-all active:scale-95 inline-flex items-center justify-center"
              >
                Поделиться в Telegram
              </button>
            </div>
          </div>
        )}

        {/* Остальные секции остаются без изменений */}
        {/* Как это работает */}
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <div className="flex items-center mb-5">
            <div className="bg-gradient-to-r from-blue-100 to-cyan-100 p-3 rounded-xl mr-3">
              <Gift className="h-6 w-6 text-blue-500" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Как работает</h2>
              <p className="text-sm text-gray-500">3 простых шага</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-orange-100 to-amber-100 rounded-full flex items-center justify-center mr-3 mt-1">
                <span className="font-bold text-orange-600">1</span>
              </div>
              <div>
                <h3 className="font-medium text-gray-800">Пригласите друга</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Отправьте ему вашу партнёрскую ссылку
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-orange-100 to-amber-100 rounded-full flex items-center justify-center mr-3 mt-1">
                <span className="font-bold text-orange-600">2</span>
              </div>
              <div>
                <h3 className="font-medium text-gray-800">Друг заказывает</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Он переходит по ссылке и делает заказ
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-orange-100 to-amber-100 rounded-full flex items-center justify-center mr-3 mt-1">
                <span className="font-bold text-orange-600">3</span>
              </div>
              <div>
                <h3 className="font-medium text-gray-800">Получайте бонусы</h3>
                <p className="text-sm text-gray-600 mt-1">
                  10% от суммы заказа на ваш баланс
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Условия */}
        <div className="bg-white rounded-2xl shadow-sm p-5 mb-8">
          <div className="flex items-center mb-5">
            <div className="bg-gradient-to-r from-purple-100 to-pink-100 p-3 rounded-xl mr-3">
              <DollarSign className="h-6 w-6 text-purple-500" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Условия программы</h2>
              <p className="text-sm text-gray-500">Основные правила</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3 mt-1">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              </div>
              <p className="text-sm text-gray-600">
                Вы получаете <span className="font-medium text-green-600">10%</span> от суммы каждого заказа приглашённого друга
              </p>
            </div>
            
            <div className="flex items-start">
              <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3 mt-1">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              </div>
              <p className="text-sm text-gray-600">
                Бонусы начисляются после успешной доставки заказа
              </p>
            </div>
            
            <div className="flex items-start">
              <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3 mt-1">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              </div>
              <p className="text-sm text-gray-600">
                Минимальная сумма для вывода: <span className="font-medium">1 000 ₽</span>
              </p>
            </div>
            
            <div className="flex items-start">
              <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3 mt-1">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              </div>
              <p className="text-sm text-gray-600">
                Вывод на карту в течение 3 рабочих дней
              </p>
            </div>
          </div>
        </div>

        {/* Промо блок */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl p-5 text-white mb-24">
          <div className="flex items-start mb-4">
            <div className="bg-white/20 p-3 rounded-xl mr-3">
              <ArrowUpRight className="h-6 w-6" />
            </div>
            <div>
              <h3 className="text-lg font-bold mb-1">Начните приглашать друзей</h3>
              <p className="text-orange-100 text-sm opacity-90">
                Чем больше друзей пригласите, тем больше заработаете
              </p>
            </div>
          </div>
          
          <button
            onClick={copyReferralLink}
            className="w-full bg-white text-orange-600 py-4 rounded-xl font-bold hover:bg-orange-50 transition-colors active:scale-95 flex items-center justify-center text-base"
          >
            <Share2 className="h-5 w-5 mr-2" />
            Скопировать партнёрскую ссылку
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReferralPage;